import { useEffect, useState } from 'react'
import { AlertCircle, Copy, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formsApi } from '@/api/formsApi'
import { buildFormCopyPayload } from '@/lib/formCopy'
import type { FormDefinition } from '@/types/cgt'

interface CopyFormDialogProps {
  open: boolean
  /** The specific version row being copied — not necessarily the family primary. */
  source: FormDefinition | null
  onOpenChange: (open: boolean) => void
  onCreated: (def: FormDefinition) => void
}

export function CopyFormDialog({ open, source, onOpenChange, onCreated }: CopyFormDialogProps) {
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Re-seed on open so a cancelled copy never leaks into the next one.
  useEffect(() => {
    if (open && source) {
      setName(`Copy of ${source.name}`)
      setError(null)
    }
  }, [open, source])

  const trimmedName = name.trim()
  const nameInvalid = !trimmedName || (!!source && trimmedName === source.name.trim())

  async function handleCreate() {
    if (!source || nameInvalid) return
    setBusy(true)
    setError(null)
    try {
      const fullSource = await formsApi.getDefinition(source.id)
      const payload = buildFormCopyPayload(fullSource, trimmedName)
      const created = await formsApi.saveDefinition(payload)
      onCreated(created)
      onOpenChange(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create the copy.')
    } finally {
      setBusy(false)
    }
  }

  if (!source) return null

  return (
    <Dialog open={open} onOpenChange={o => !busy && onOpenChange(o)}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Copy Form</DialogTitle>
          <DialogDescription>
            Creates a brand-new form with its own id and version family — the fields, sections,
            and gates of &ldquo;{source.name}&rdquo; are duplicated, not linked.
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-1.5 py-2'>
          <Label htmlFor='copy-form-name'>Name</Label>
          <Input
            id='copy-form-name'
            value={name}
            onChange={e => setName(e.target.value)}
            autoFocus
          />
          {nameInvalid && (
            <p className='text-xs text-muted-foreground'>
              Give the copy a name different from the source form.
            </p>
          )}
          {error && (
            <p className='flex items-start gap-1.5 text-sm text-destructive'>
              <AlertCircle className='h-4 w-4 shrink-0 mt-0.5' />
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={busy || nameInvalid}>
            {busy ? (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            ) : (
              <Copy className='mr-2 h-4 w-4' />
            )}
            Create Copy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
